import React, { useEffect, useState } from 'react';
import { Sparkles, Trash2, Pencil, Save, X, EyeOff, Eye } from 'lucide-react';
import api from '../lib/api';
import HelpCallout from './HelpCallout';

export default function AdminDigest() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // digest being edited
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  function load() { api.get('/admin/digests').then((r) => setItems(r.data.items)); }
  useEffect(load, []);

  async function generate() {
    setLoading(true);
    try {
      await api.post('/admin/digest/generate');
      load();
    } catch (e) {
      alert('Generation failed: ' + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }

  async function del(id) {
    if (!window.confirm('Delete digest?')) return;
    await api.delete(`/admin/digests/${id}`); load();
  }

  async function saveEdit() {
    setSaving(true); setErr('');
    try {
      await api.patch(`/admin/digests/${editing.id}`, { title: editing.title, intro: editing.intro, body: editing.body });
      setEditing(null); load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  }

  async function togglePublished(d) {
    try {
      await api.patch(`/admin/digests/${d.id}`, { published: !d.published });
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Update failed'); }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="label text-heritage mb-2">Weekly digest</div>
          <h1 className="font-display text-4xl">AI-written. Editor-approved.</h1>
        </div>
        <button onClick={generate} disabled={loading} data-testid="digest-generate"
          className="bg-charcoal text-base px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-charcoal/90 disabled:opacity-60">
          <Sparkles size={14}/> {loading ? 'Writing this week\'s digest...' : 'Generate new digest'}
        </button>
      </div>

      <HelpCallout
        title="A weekly editorial piece written by Claude AI — using your real data."
        description="Click 'Generate' and the AI writes a punchy weekend digest based on this week's approved events + the current weather forecast. The newest digest auto-appears on the homepage and at /digest, and is the source material that the Friday newsletter automation builds on. You can edit any digest's wording, or unpublish it to pull it off the site instantly."
        appearsAt={[
          { label: 'Homepage "AI Weekend Digest" block', where: 'always shows the most recently generated digest' },
          { label: '/digest page', where: 'latest plus archive of past digests' },
          { label: 'Friday auto-newsletter', where: 'uses the latest digest as the body each Friday 07:00' },
        ]}
      />

      <div className="space-y-4">
        {items.map((d) => (
          <article key={d.id} className="bg-white border border-charcoal/10 rounded-2xl p-6" data-testid={`digest-${d.id}`}>
            <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
              <div className="label text-charcoal/50">Week of {new Date(d.week_starting).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
              <div className="flex items-center gap-2">
                {d.sentinel_held && <span className="text-xs bg-heritage/10 text-heritage font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1"><EyeOff size={11}/> Held by reassurance filter</span>}
                {!d.published && !d.sentinel_held && <span className="text-xs bg-charcoal/10 font-semibold px-2.5 py-1 rounded-full">Unpublished</span>}
                <button onClick={() => togglePublished(d)} data-testid={`digest-toggle-${d.id}`}
                  title={d.published ? 'Take off the site' : 'Put back on the site'}
                  className="p-1.5 hover:bg-admin rounded-lg text-charcoal/70">
                  {d.published ? <EyeOff size={14}/> : <Eye size={14}/>}
                </button>
                <button onClick={() => { setErr(''); setEditing({ ...d }); }} data-testid={`digest-edit-${d.id}`} className="p-1.5 hover:bg-admin rounded-lg text-charcoal/70"><Pencil size={14}/></button>
                <button onClick={() => del(d.id)} data-testid={`digest-delete-${d.id}`} className="p-1.5 text-heritage hover:bg-heritage/5 rounded-lg"><Trash2 size={14}/></button>
              </div>
            </div>

            {editing?.id === d.id ? (
              <div className="space-y-3" data-testid={`digest-editor-${d.id}`}>
                <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="digest-edit-title"
                  className="w-full bg-admin border border-charcoal/10 rounded-xl px-3 py-2 font-display text-xl"/>
                <textarea rows={2} value={editing.intro} onChange={(e) => setEditing({ ...editing, intro: e.target.value })} data-testid="digest-edit-intro"
                  className="w-full bg-admin border border-charcoal/10 rounded-xl px-3 py-2 text-sm"/>
                <textarea rows={12} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} data-testid="digest-edit-body"
                  className="w-full bg-admin border border-charcoal/10 rounded-xl px-3 py-2 text-sm leading-relaxed"/>
                {err && <div className="text-sm text-heritage bg-heritage/5 border border-heritage/30 rounded-xl px-3 py-2" data-testid="digest-edit-error">{err}</div>}
                <div className="flex gap-3">
                  <button onClick={saveEdit} disabled={saving} data-testid="digest-edit-save"
                    className="bg-forest text-base px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"><Save size={14}/> {saving ? 'Saving…' : 'Save changes'}</button>
                  <button onClick={() => setEditing(null)} data-testid="digest-edit-cancel"
                    className="bg-white border border-charcoal/15 px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2"><X size={14}/> Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <h3 className="font-display text-2xl mb-2">{d.title}</h3>
                <p className="text-charcoal/85 font-display text-lg leading-snug mb-3">{d.intro}</p>
                <p className="text-muted text-sm whitespace-pre-wrap line-clamp-4">{d.body}</p>
                {d.weather_summary && <div className="mt-4 label text-charcoal/40">☁︎ {d.weather_summary}</div>}
              </>
            )}
          </article>
        ))}
        {items.length === 0 && <div className="text-center py-16 text-muted">No digests yet. Generate your first one above.</div>}
      </div>
    </div>
  );
}
